import React, { useEffect, useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Shared column-header sorting for every dashboard table.
 *
 * Two consumers:
 *  - Client-side tables (whole dataset already in memory): wrap the rows with
 *    the useSortableData hook and render <SortableHeader> cells. Sorting happens
 *    in the browser via a smart comparator (numbers, dates, then text).
 *  - Server-paginated tables (one page fetched at a time): keep sort state next
 *    to the page state, pass it to <SortableHeader>, and refetch with the
 *    sort/dir query params on change. The same header component drives both.
 */

// Recognise the two date string shapes the repositories emit: ISO 'YYYY-MM-DD'
// (form/CRUD rows) and legacy 'DD/MM/YYYY' (dashboard stats rows).
const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;
const DMY_DATE = /^(\d{2})\/(\d{2})\/(\d{4})$/;

const toComparableDate = (value) => {
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value.getTime();
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (ISO_DATE.test(trimmed)) {
    const t = new Date(trimmed).getTime();
    return isNaN(t) ? null : t;
  }
  const dmy = DMY_DATE.exec(trimmed);
  if (dmy) {
    const [, d, m, y] = dmy;
    return new Date(Number(y), Number(m) - 1, Number(d)).getTime();
  }
  return null;
};

// Numeric only when the whole value is a number (avoids treating an id-prefixed
// string as numeric). Empty strings are not numbers.
const toComparableNumber = (value) => {
  if (typeof value === 'number') return isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const n = Number(trimmed);
    return isNaN(n) || !isFinite(n) ? null : n;
  }
  return null;
};

/**
 * Compare two cell values. Blanks sink to the bottom regardless of direction;
 * numbers compare numerically, date strings chronologically, everything else
 * as case-insensitive text.
 */
export const compareValues = (a, b) => {
  const aBlank = a === null || a === undefined || a === '';
  const bBlank = b === null || b === undefined || b === '';
  if (aBlank && bBlank) return 0;
  if (aBlank) return 1;
  if (bBlank) return -1;

  const aDate = toComparableDate(a);
  const bDate = toComparableDate(b);
  if (aDate !== null && bDate !== null) return aDate - bDate;

  const aNum = toComparableNumber(a);
  const bNum = toComparableNumber(b);
  if (aNum !== null && bNum !== null) return aNum - bNum;

  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

/**
 * Client-side sorting hook.
 * @param {Array} items - the full row set already in memory
 * @param {{key: string, direction: 'asc'|'desc', accessor?: Function}|null} initial
 * @returns {{sorted: Array, sortConfig, requestSort: Function}}
 *   requestSort(key, accessor?) toggles asc/desc (first click on a new column is asc).
 */
export const useSortableData = (items, initial = null) => {
  const [sortConfig, setSortConfig] = useState(initial);

  const sorted = useMemo(() => {
    if (!sortConfig || !sortConfig.key) return items;
    const get = sortConfig.accessor || ((row) => row[sortConfig.key]);
    const factor = sortConfig.direction === 'desc' ? -1 : 1;
    // Stable sort: decorate with the original index as the tie-breaker.
    return items
      .map((item, index) => ({ item, index }))
      .sort((a, b) => {
        const cmp = compareValues(get(a.item), get(b.item));
        return cmp !== 0 ? cmp * factor : a.index - b.index;
      })
      .map((d) => d.item);
  }, [items, sortConfig]);

  const requestSort = (key, accessor) => {
    setSortConfig((prev) =>
      prev && prev.key === key
        ? { key, accessor, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, accessor, direction: 'asc' }
    );
  };

  return { sorted, sortConfig, requestSort };
};

/**
 * How many rows a table shows at once, everywhere — the house rule: every
 * table sorts by column and shows 50 with paging. Server-paginated pages send
 * this as their page size; client-side tables slice with it.
 */
export const PAGE_SIZE = 50;

/**
 * Client-side sorting + paging in one call — the default for any table whose
 * rows are already in memory.
 *
 * `items` should be the filtered row set; paging resets to page 1 whenever the
 * row count or the sort changes (a filter that leaves the count untouched keeps
 * your place, which is what you want when ticking a checkbox mid-list).
 *
 * @returns everything useSortableData returns, plus `rows` (the current page),
 *   `page`/`setPage`/`totalPages`/`total`/`pageSize` for <TablePager>.
 */
export const usePagedSortableData = (items, initial = null, pageSize = PAGE_SIZE) => {
  const { sorted, sortConfig, requestSort } = useSortableData(items, initial);
  const [page, setPage] = useState(1);

  useEffect(() => { setPage(1); }, [items.length, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  // Clamp rather than reset: deleting the last row of page 3 lands on page 2.
  const safePage = Math.min(page, totalPages);
  const rows = useMemo(
    () => sorted.slice((safePage - 1) * pageSize, safePage * pageSize),
    [sorted, safePage, pageSize]
  );

  return {
    rows, sorted, sortConfig, requestSort,
    page: safePage, setPage, totalPages, total: sorted.length, pageSize,
  };
};

/**
 * The one pagination bar. Renders nothing while everything fits on one page.
 * Works for both client-side tables (pass the hook's values) and
 * server-paginated ones (pass the page state and the API's total).
 */
export const TablePager = ({
  page, setPage, total, pageSize = PAGE_SIZE,
  className = 'flex items-center justify-between pt-3 mt-3 border-t border-gray-200',
  noun,
}) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) return null;
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <div className={className}>
      <span className="text-sm text-gray-600">
        {first}–{last} of {total}{noun ? ` ${noun}` : ''}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setPage(Math.max(1, page - 1))}
          disabled={page <= 1}
          className="inline-flex items-center gap-1 px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-4 h-4" /> Prev
        </button>
        <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
        <button
          type="button"
          onClick={() => setPage(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          className="inline-flex items-center gap-1 px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Next <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const SortIcon = ({ direction }) => {
  if (direction === 'asc') return <ChevronUp className="h-3.5 w-3.5" />;
  if (direction === 'desc') return <ChevronDown className="h-3.5 w-3.5" />;
  return <ChevronsUpDown className="h-3.5 w-3.5 opacity-30 group-hover:opacity-60" />;
};

/**
 * A sortable <th>. Drop-in replacement for a plain header cell.
 * @param {string} label - header text
 * @param {string} sortKey - identifies the column in sortConfig / the server sort param
 * @param {object|null} sortConfig - {key, direction} the parent tracks
 * @param {Function} onSort - (sortKey, accessor) => void
 * @param {Function} [accessor] - value getter for client-side sorting (ignored server-side)
 * @param {'left'|'right'} [align='left']
 * @param {boolean} [sortable=true] - false renders a plain, non-clickable header (e.g. Actions)
 * @param {string} [className] - full th class list; falls back to the shared default
 */
export const SortableHeader = ({
  label,
  sortKey,
  sortConfig,
  onSort,
  accessor,
  align = 'left',
  sortable = true,
  className,
}) => {
  const base = className || 'px-4 py-2 text-xs font-semibold text-slate-500 uppercase';
  const alignCls = align === 'right' ? 'text-right' : 'text-left';

  if (!sortable) {
    return <th className={`${base} ${alignCls}`}>{label}</th>;
  }

  const active = sortConfig && sortConfig.key === sortKey;
  const direction = active ? sortConfig.direction : null;

  return (
    <th
      onClick={() => onSort(sortKey, accessor)}
      aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={`${base} ${alignCls} group cursor-pointer select-none hover:bg-slate-100`}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {label}
        <SortIcon direction={direction} />
      </span>
    </th>
  );
};

export default SortableHeader;